/**
 * Lines API Service
 *
 * Lines are authored in the UI (snapshot-based) and later referenced by policies
 * like line_crossing. Geometry is points_norm: [[x,y],[x,y]].
 */

import { api } from './api';

export interface Line {
  id: string;
  line_id: string;
  name: string;
  site_id?: string;
  camera_id?: string;
  device_id?: string;
  points_norm: number[][];
  color?: string; // '#00FFFF' | '#FF00FF'
  thickness_px?: number; // pixels (used by debug overlay)
  enabled?: boolean;
  version?: number;
  updated_at?: string;
}

export interface LineCreateData {
  line_id: string;
  name: string;
  device_id: string;
  camera_id: string;
  points_norm: number[][];
  color?: string;
  thickness_px?: number;
  enabled?: boolean;
  site_id?: string;
}

export const linesApi = {
  listLines: async (
    deviceId?: string,
    cameraId?: string,
    includeDisabled?: boolean
  ): Promise<{ lines: Line[]; count: number }> => {
    const params: Record<string, any> = {};
    if (deviceId) params.device_id = deviceId;
    if (cameraId) params.camera_id = cameraId;
    if (includeDisabled !== undefined) params.include_disabled = includeDisabled;
    const response = await api.get('/api/v1/lines', { params });
    return response.data;
  },

  createLine: async (data: LineCreateData): Promise<any> => {
    const response = await api.post('/api/v1/lines', data);
    return response.data;
  },

  updateLine: async (idOrLineId: string, data: Partial<LineCreateData>): Promise<any> => {
    const response = await api.put(`/api/v1/lines/${idOrLineId}`, data);
    return response.data;
  },

  deleteLine: async (idOrLineId: string): Promise<any> => {
    const response = await api.delete(`/api/v1/lines/${idOrLineId}`);
    return response.data;
  },

  /**
   * Phase F: validate lines bundle (deployment-correlated API)
   */
  validateLines: async (
    siteId: string,
    payload: { org_id?: string; edge_device_id?: string; camera_id?: string; line_ids?: string[] }
  ): Promise<any> => {
    const response = await api.post(`/api/v1/sites/${siteId}/lines/validate`, payload);
    return response.data;
  },

  /**
   * Phase F: deploy lines bundle (deployment-correlated API)
   */
  deployLines: async (
    siteId: string,
    payload: { org_id?: string; edge_device_id?: string; camera_ids?: string[]; line_ids?: string[]; initiated_by?: string }
  ): Promise<any> => {
    const response = await api.post(`/api/v1/sites/${siteId}/lines/deploy`, payload);
    return response.data;
  },
};


